import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthNav } from '../AuthNav';
import { APP_NAME } from '../../utils/env';
import { useUser } from '@clerk/clerk-react';
import useAdminAuth from '../../hooks/useAdminAuth';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  
  // Use Clerk's useUser hook
  const { isSignedIn } = useUser();
  
  // Use our custom admin hook for consistent admin checking
  const { isAdmin } = useAdminAuth();
  
  const isActive = (path: string) => location.pathname === path;

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <nav className="bg-white shadow">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 justify-between">
          {/* Logo and desktop navigation */}
          <div className="flex">
            <Link to="/" className="flex flex-shrink-0 items-center">
              <span className="text-xl font-bold text-primary-600">{APP_NAME}</span>
            </Link>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              <Link
                to="/"
                className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                  isActive('/') 
                    ? 'border-b-2 border-primary-500 text-gray-900'
                    : 'border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                Home
              </Link>
              <Link
                to="/post-job"
                className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                  isActive('/post-job')
                    ? 'border-b-2 border-primary-500 text-gray-900'
                    : 'border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                Post Job
              </Link>
              <Link
                to="/jobs"
                className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                  isActive('/jobs')
                    ? 'border-b-2 border-primary-500 text-gray-900'
                    : 'border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                Browse Jobs
              </Link>

              {/* Admin link - only shown if user is admin */}
              {isSignedIn && isAdmin && (
                <Link
                  to="/admin"
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
                    isActive('/admin') || location.pathname.startsWith('/admin/')
                      ? 'border-b-2 border-primary-500 text-gray-900'
                      : 'border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  Admin
                </Link>
              )}
            </div>
          </div>
          
          {/* Desktop auth navigation */}
          <div className="hidden sm:flex sm:items-center sm:space-x-4">
            {/* Authentication Navigation */}
            <AuthNav />
          </div>
          
          {/* Mobile menu button */}
          <div className="flex items-center sm:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
              aria-controls="mobile-menu"
              aria-expanded="false"
              onClick={toggleMenu}
            >
              <span className="sr-only">Open main menu</span>
              {/* Icon when menu is closed */}
              <svg
                className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              {/* Icon when menu is open */}
              <svg
                className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu, show/hide based on menu state */}
      <div className={`${isMenuOpen ? 'block' : 'hidden'} sm:hidden`} id="mobile-menu">
        <div className="space-y-1 pb-3 pt-2">
          <Link 
            to="/"
            className={`block border-l-4 py-2 pl-3 pr-4 text-base font-medium ${
              isActive('/')
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700'
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            Home
          </Link>
          <Link 
            to="/jobs"
            className={`block border-l-4 py-2 pl-3 pr-4 text-base font-medium ${
              isActive('/jobs')
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700'
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            Browse Jobs
          </Link>
          <Link 
            to="/post-job"
            className={`block border-l-4 py-2 pl-3 pr-4 text-base font-medium ${
              isActive('/post-job')
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700'
            }`}
            onClick={() => setIsMenuOpen(false)}
          >
            Post a Job
          </Link>

          {/* Admin link for mobile - only shown if user is admin */}
          {isSignedIn && isAdmin && (
            <Link 
              to="/admin"
              className={`block border-l-4 py-2 pl-3 pr-4 text-base font-medium ${
                isActive('/admin') || location.pathname.startsWith('/admin/')
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Admin
            </Link>
          )}
        </div>
        
        {/* Auth links for mobile */}
        <div className="border-t border-gray-200 pt-4 pb-3">
          <div className="flex items-center px-4">
            <AuthNav />
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
